import { Info } from 'phosphor-icons';
import { c } from '../../utils/tailwind-merge';
import { LinkButton } from '../Link/LinkButton';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableRow,
} from '../Table/Table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../Tooltip/TooltipRadix';
import { PublisherInfoTableProps } from './types';

export const PublisherInfoTable = ({
  supportContact,
  website,
  organizationName,
  publisherProfile,
  duns,
  address,
  country,
  microcopy,
  className = '',
  rightColumnTextAlign = 'right',
}: PublisherInfoTableProps) => {
  return (
    <TooltipProvider>
      <Table className={c('w-full !table-fixed', className)}>
        <colgroup>
          <col className="sm:w-[280px] w-[170px] md:w-[190px]" />
          <col className="w-auto" />
        </colgroup>

        {microcopy.title && <TableCaption>{microcopy.title}</TableCaption>}

        <TableBody
          className="
            [&>tr>td:first-child]:whitespace-normal
            [&>tr>td:first-child]:break-words
            [&>tr>td]:py-1.5
          "
          rightColTextAlign={rightColumnTextAlign}
        >
          {supportContact && microcopy.supportContact && (
            <TableRow>
              <TableCell className="pr-1">{microcopy.supportContact}</TableCell>
              <TableCell>{supportContact}</TableCell>
            </TableRow>
          )}

          {website && (
            <TableRow>
              <TableCell className="pr-1">{microcopy.website}</TableCell>
              <TableCell>
                <LinkButton
                  className="inline-block font-normal lowercase text-blue no-underline"
                  openLinkInNewTab
                  label={website}
                  href={website}
                  hasIcon
                />
              </TableCell>
            </TableRow>
          )}

          {organizationName && microcopy.verifiedOrganizationName && (
            <TableRow className="align-top">
              <TableCell className="pr-1">
                <div
                  className="
                    block
                    whitespace-normal           
                    break-words                  
                    leading-snug
                    [word-break:normal]
                  "
                >
                  {microcopy.verifiedOrganizationName}
                </div>
              </TableCell>

              <TableCell className="align-top">{organizationName}</TableCell>
            </TableRow>
          )}

          {publisherProfile && microcopy.publisherDetails && (
            <TableRow>
              <TableCell className="pr-1">
                {microcopy.publisherDetails}
              </TableCell>
              <TableCell className="[&_a]:no-underline [&_span]:no-underline">
                <LinkButton
                  className="inline-block font-normal capitalize text-blue no-underline"
                  openLinkInNewTab
                  label={microcopy.viewPublisherDetails}
                  href={publisherProfile}
                />
              </TableCell>
            </TableRow>
          )}

          {duns && (
            <TableRow>
              <TableCell className="pr-1">
                <div className="flex items-center gap-1">
                  {microcopy.duns}
                  {microcopy.dunsHelp && (
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-4 w-4 text-grey-500" />
                      </TooltipTrigger>
                      <TooltipContent className="text-sm text-grey-700">
                        {microcopy.dunsHelp}
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
              </TableCell>
              <TableCell>{duns}</TableCell>
            </TableRow>
          )}

          {address && microcopy.address && (
            <TableRow>
              <TableCell className="pr-1">{microcopy.address}</TableCell>
              <TableCell>
                <div className="space-y-1">
                  {address.name && <div>{address.name}</div>}
                  {address.street && <div>{address.street}</div>}
                  <div>
                    {address.city}
                    {address.state && `, ${address.state}`}
                    {address.zipCode && ` ${address.zipCode}`}
                  </div>
                  {address.country && <div>{address.country}</div>}
                </div>
              </TableCell>
            </TableRow>
          )}

          {country && microcopy.country && (
            <TableRow>
              <TableCell className="pr-1">{microcopy.country}</TableCell>
              <TableCell>{country}</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TooltipProvider>
  );
};
